import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import useAddPostForm from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleSourceType,
  ArticleFieldsType,
  ArticleType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import useProjectDetailsQuery from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { Divider, Box, IconButton, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from 'config/theme';
import useUserData from 'hooks/useUserData';
import { useRef, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import { useProjectRetroContext } from '../../context';
import AddLessonForm from './AddLessonForm';
import RenderRetrospective from './RenderRetrospective';
import { ReactComponent as RetrospectiveFillIcon } from 'assets/icons/RetrospectiveFillIcon.svg';
import RetrosHeader from './RetrosHeader';
import LessonsTable from './LessonsTable';
import RetroSummarizedResults from './RetroSummarizedResults';
import ViewLearning from './ViewLearning';

function Retrospectives() {
  const {
    state: {
      isFullScreen,
      isOpenedLessonForm,
      selectedArticle,
      view,
      selectedLessonType,
      articleIds,
    },
    dispatch,
  } = useProjectRetroContext();
  const { companyId } = useUserData();
  const { activeUniqueEntries: phasesAndMilestones } = useConfigQuery({
    companyId,
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const { projectId = '' } = useParams();
  const { project, refetch, projectArticles, isLoading } = useProjectDetailsQuery({
    projectId,
    shouldFetch: false,
  });
  const { articleFields, isLoadingArticleConfigFields, loadingFiles, postForm, onDrop, onSubmit } =
    useAddPostForm({
      setOpen: () => {
        dispatch({
          payload: {
            isOpenedLessonForm: false,
          },
        });
      },
      articleType: ArticleSourceType.LESSON,
      articleFieldsType: ArticleFieldsType.LESSON,
      defaultProjectId: projectId,
      data: selectedArticle,
      isEditing: !!selectedArticle,
      articleId: selectedArticle?.id || '',
      updateViewCount: !!selectedArticle?.id,
      updateArticleCallback: ({ updatedArticle }) => {
        dispatch({
          payload: {
            selectedArticle: updatedArticle,
          },
        });
        refetch();
      },
      onAddArticleCallback: ({ article }) => {
        dispatch({
          payload: {
            selectedArticle: article,
          },
        });
        refetch();
      },
    });
  const { pluralRetrospectivesModuleName } = useBuildWiseModules();
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lessons = useMemo(() => {
    if (!projectArticles || !phasesAndMilestones) return [];

    const phaseOnly: ArticleType[] = [];
    const milestoneOnly: ArticleType[] = [];
    const noPhaseNoMilestone: ArticleType[] = [];

    const filteredArticles = projectArticles.filter(
      (article) =>
        !selectedLessonType?.id ||
        article.lessonTypes?.includes(selectedLessonType?.id) ||
        (!article.lessonTypes && selectedLessonType?.id === 'Blank'),
    );

    const milestoneMap = new Map<string, ArticleType[]>();

    phasesAndMilestones.forEach((phase) => {
      phase.entryValues?.forEach((milestone) => {
        const articlesForMilestone = filteredArticles
          .filter((article) => article.projectMileStones?.includes(milestone))
          .map((article) => ({
            ...article,
            projectPhases: [phase.entryCode],
            projectMileStones: [milestone],
          }));

        if (articlesForMilestone.length) {
          milestoneMap.set(milestone, articlesForMilestone);
        }
      });
    });

    const phaseAndMilestone = Array.from(milestoneMap.values()).flat();

    filteredArticles.forEach((article) => {
      if (!phaseAndMilestone.some((a) => a.id === article.id)) {
        if (article.projectPhases?.length && !article.projectMileStones?.length) {
          phaseOnly.push(article);
        } else if (!article.projectPhases?.length && article.projectMileStones?.length) {
          milestoneOnly.push(article);
        } else if (!article.projectPhases?.length && !article.projectMileStones?.length) {
          noPhaseNoMilestone.push(article);
        }
      }
    });

    phaseOnly.sort((a, b) =>
      (a.projectPhases?.[0] || '').localeCompare(b.projectPhases?.[0] || ''),
    );
    milestoneOnly.sort((a, b) =>
      (a.projectMileStones?.[0] || '').localeCompare(b.projectMileStones?.[0] || ''),
    );

    return [...phaseAndMilestone, ...phaseOnly, ...milestoneOnly, ...noPhaseNoMilestone];
  }, [phasesAndMilestones, projectArticles, selectedLessonType]);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        // Get the container's top position relative to the viewport
        const containerTop = containerRef.current.getBoundingClientRect().top;
        // Calculate height from its current position to the bottom of the viewport
        containerRef.current.style.height = `${window.innerHeight - containerTop}px`;
      }
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight);
    };
  }, [containerRef.current, articleIds]);

  useEffect(() => {
    const updateHeight = () => {
      if (parentContainerRef.current) {
        if (isFullScreen) {
          parentContainerRef.current.style.maxHeight = 'fit-content';
        } else {
          const containerTop = parentContainerRef.current.getBoundingClientRect().top;
          parentContainerRef.current.style.height = `${window.innerHeight - containerTop}px`;
          parentContainerRef.current.style.maxHeight = `${window.innerHeight - 200}px`;
        }
      }
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight);
    };
  }, [parentContainerRef.current, view, isFullScreen]);

  return (
    <FormProvider {...postForm}>
      <Box ref={parentContainerRef}>
        {!isFullScreen && (
          <Box
            sx={(theme) => ({
              position: 'sticky',
              top: 140,
              backgroundColor: theme.palette.custom.background,
              zIndex: 9,
              pb: 1,
            })}
          >
            <RetrosHeader filteredLessons={lessons} />
          </Box>
        )}
        <RetrospectivesContainer
          sx={{
            ...(isFullScreen && {
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              right: 0,
              zIndex: 99,
              p: 0,
              overflow: 'auto',
            }),
          }}
        >
          {isFullScreen && (
            <>
              <StickyContainer>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={(theme) => ({
                    backgroundColor: theme.palette.custom.white,
                    zIndex: 99,
                    px: 1,
                  })}
                >
                  <Box display="flex" alignItems="flex-start" p={2} gap={1} pb={1}>
                    <IconButton
                      onClick={() =>
                        dispatch(({ state }) => ({
                          payload: {
                            ...state,
                            isFullScreen: !state.isFullScreen,
                          },
                        }))
                      }
                    >
                      <ArrowLeftLineIcon color={theme.palette.custom.navy} />
                    </IconButton>
                    <ProjectIconContainer>
                      <BuildingLineIcon color={theme.palette.custom.black} size={24} />
                    </ProjectIconContainer>
                    <Box ml={1}>
                      <Title>{project.name}</Title>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={(theme) => ({ '& svg path': { fill: theme.palette.custom.mistBlue } })}
                      >
                        <RetrospectiveFillIcon width={20} />
                        <Title
                          sx={(theme) => ({ color: theme.palette.custom.mistBlue, fontSize: 16 })}
                        >
                          {pluralRetrospectivesModuleName}
                        </Title>
                      </Box>
                    </Box>
                  </Box>
                  <RetrosHeader filteredLessons={lessons} />
                </Box>
                <Divider />
              </StickyContainer>
            </>
          )}

          <Box p={isFullScreen ? 1 : 0}>
            <LessonsTable lessons={lessons} isLoading={isLoading} />
          </Box>
        </RetrospectivesContainer>
      </Box>

      {(isOpenedLessonForm || selectedArticle) && (
        <Box
          ref={containerRef}
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            bottom: 0,
            right: 0,
            width: 800,
            backgroundColor: theme.palette.custom.white,
            borderLeft: `1px solid ${theme.palette.custom.divider}`,
            boxShadow: `-6px 0 12px 0 ${theme.palette.custom.boxShadow}`,
            overflow: 'auto',
            zIndex: 99,
          })}
        >
          {!!articleIds.length ? (
            <RetroSummarizedResults />
          ) : isOpenedLessonForm ? (
            <AddLessonForm
              articleFields={articleFields}
              articleFieldsLoading={isLoadingArticleConfigFields}
              loadingFiles={loadingFiles}
              onDrop={onDrop}
              onSubmit={onSubmit}
            />
          ) : (
            <ViewLearning />
          )}
        </Box>
      )}
    </FormProvider>
  );
}

export const RetrospectivesContainer = styled(Paper)(({ theme }) => ({
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  padding: 8,
  paddingBottom: 0,
  borderRadius: 8,
}));

const StickyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: 'sticky',
  top: 0,
  zIndex: 9,
}));

const ProjectIconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.lightBlue,
  width: 48,
  height: 48,
  borderRadius: 5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
}));

export default Retrospectives;
